// src/pages/LandingPage.js
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Grid,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import {
  CheckCircle as CheckIcon,
  Route as RouteIcon,
  Schedule as ScheduleIcon,
  People as PeopleIcon,
  Analytics as AnalyticsIcon,
  Shield as ShieldIcon,
  LocalHospital as HospitalIcon,
  VerifiedUser as VerifiedIcon,
  MedicalServices as MedicalIcon,
  Assignment as AssignmentIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  KeyboardArrowDown as ArrowDownIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";

// Color palette
const colors = {
  primary: "#0369A1",       // medical blue
  primaryDark: "#075985",
  primaryLight: "#E0F2FE",
  accent: "#0891B2",        // teal
  accentLight: "#ECFEFF",
  success: "#059669",
  successLight: "#D1FAE5",
  text: "#1E293B",
  textSecondary: "#64748B",
  border: "#E2E8F0",
  bgWhite: "#FFFFFF",
  bgLight: "#F8FAFC",
  bgSection: "#F1F5F9",
};

const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

  .med-animate {
    opacity: 0;
    transform: translateY(24px);
    transition: opacity 0.6s ease, transform 0.6s ease;
  }

  .med-animate.visible {
    opacity: 1;
    transform: translateY(0);
  }

  .stagger-1 { transition-delay: 0.1s; }
  .stagger-2 { transition-delay: 0.2s; }
  .stagger-3 { transition-delay: 0.3s; }
  .stagger-4 { transition-delay: 0.4s; }
  .stagger-5 { transition-delay: 0.5s; }
  .stagger-6 { transition-delay: 0.6s; }
`;

if (typeof document !== "undefined") {
  const existingStyle = document.getElementById("landing-styles");
  if (!existingStyle) {
    const styleSheet = document.createElement("style");
    styleSheet.id = "landing-styles";
    styleSheet.innerText = globalStyles;
    document.head.appendChild(styleSheet);
  }
}

const useScrollAnimation = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );

    document.querySelectorAll(".med-animate").forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);
};

const AnimatedCounter = ({ end, duration = 2000, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          let start = 0;
          const increment = end / (duration / 16);
          const timer = setInterval(() => {
            start += increment;
            if (start >= end) {
              setCount(end);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 16);
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration, hasAnimated]);

  return (
    <span ref={ref}>
      {count.toLocaleString()}
      {suffix}
    </span>
  );
};

const LandingPage = () => {
  useScrollAnimation();

  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    setFormSubmitted(true);
    setContactForm({ name: "", email: "", message: "" });
    setTimeout(() => setFormSubmitted(false), 3000);
  };

  const features = [
    {
      icon: RouteIcon,
      title: "Route Optimization",
      desc: "Automatically plan the most efficient routes for home health visits. Reduce drive time, see more patients, and lower fuel costs with intelligent geographic clustering.",
      color: colors.primary,
      bg: colors.primaryLight,
    },
    {
      icon: ScheduleIcon,
      title: "Order Management",
      desc: "Create, assign, and track medical service orders from intake to completion. Manage exams, diagnoses, and physician referrals with full audit trails.",
      color: colors.accent,
      bg: colors.accentLight,
    },
    {
      icon: PeopleIcon,
      title: "Staff Coordination",
      desc: "Assign nurses and technicians based on location, skills, and availability. Track your field team in real time and balance workloads across your organization.",
      color: colors.primary,
      bg: colors.primaryLight,
    },
    {
      icon: AnalyticsIcon,
      title: "Performance Analytics",
      desc: "Monitor order completion rates, response times, and operational efficiency. Get alerts when metrics fall below thresholds and track trends over time.",
      color: colors.accent,
      bg: colors.accentLight,
    },
    {
      icon: ShieldIcon,
      title: "HIPAA Compliance",
      desc: "Field-level encryption for protected health information, complete audit logs with 7-year retention, two-factor authentication, and role-based access control.",
      color: colors.success,
      bg: colors.successLight,
    },
    {
      icon: AssignmentIcon,
      title: "Document Processing",
      desc: "Upload, scan, and extract data from medical documents using OCR. Handle eFax intake, PDF processing, and securely store patient records with encryption.",
      color: colors.primary,
      bg: colors.primaryLight,
    },
  ];

  const stats = [
    { value: 500, suffix: "+", label: "Healthcare Facilities" },
    { value: 2, suffix: "M+", label: "Patients Managed" },
    { value: 40, suffix: "%", label: "Reduction in Drive Time" },
    { value: 99.9, suffix: "%", label: "Platform Uptime" },
  ];

  const steps = [
    {
      step: "1",
      title: "Register Your Facility",
      desc: "Set up your organization, add locations, and configure service areas. Import your staff roster with roles and credentials.",
    },
    {
      step: "2",
      title: "Enter Patient Orders",
      desc: "Create service orders with patient details, required exams, and physician information. Attach documents via upload or eFax.",
    },
    {
      step: "3",
      title: "Assign & Optimize Routes",
      desc: "The system groups orders by geography and assigns them to available medical professionals. Routes are optimized to minimize travel.",
    },
    {
      step: "4",
      title: "Track & Report",
      desc: "Monitor order progress in real time. Review completion rates, staff performance, and compliance reports from your dashboard.",
    },
  ];

  return (
    <Box
      sx={{
        background: colors.bgWhite,
        minHeight: "100vh",
        color: colors.text,
        fontFamily: '"Inter", sans-serif',
      }}
    >
      {/* Navigation Bar */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          background: "rgba(255,255,255,0.95)",
          backdropFilter: "blur(8px)",
          borderBottom: `1px solid ${colors.border}`,
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              py: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <MedicalIcon sx={{ color: colors.primary, fontSize: 32 }} />
              <Typography
                sx={{
                  fontSize: "1.4rem",
                  fontWeight: 700,
                  color: colors.primary,
                  letterSpacing: "-0.02em",
                }}
              >
                MedFlowHub
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
              <Typography
                component="a"
                href="#features"
                sx={{
                  color: colors.textSecondary,
                  textDecoration: "none",
                  fontSize: 15,
                  fontWeight: 500,
                  display: { xs: "none", md: "block" },
                  "&:hover": { color: colors.primary },
                }}
              >
                Features
              </Typography>
              <Typography
                component="a"
                href="#how-it-works"
                sx={{
                  color: colors.textSecondary,
                  textDecoration: "none",
                  fontSize: 15,
                  fontWeight: 500,
                  display: { xs: "none", md: "block" },
                  "&:hover": { color: colors.primary },
                }}
              >
                How It Works
              </Typography>
              <Typography
                component="a"
                href="#contact"
                sx={{
                  color: colors.textSecondary,
                  textDecoration: "none",
                  fontSize: 15,
                  fontWeight: 500,
                  display: { xs: "none", md: "block" },
                  "&:hover": { color: colors.primary },
                }}
              >
                Contact
              </Typography>
              <Button
                component={RouterLink}
                to="/login"
                sx={{
                  background: colors.primary,
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: 14,
                  px: 3,
                  py: 1,
                  borderRadius: "8px",
                  textTransform: "none",
                  "&:hover": {
                    background: colors.primaryDark,
                  },
                }}
              >
                Sign In
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box
        sx={{
          pt: { xs: 14, md: 18 },
          pb: { xs: 10, md: 14 },
          background: `linear-gradient(180deg, ${colors.primaryLight} 0%, ${colors.bgWhite} 100%)`,
          position: "relative",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid  size={{ xs: 12, md: 7 }}>
              {/* HIPAA Badge */}
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  background: colors.successLight,
                  border: `1px solid #A7F3D0`,
                  borderRadius: "100px",
                  px: 2,
                  py: 0.75,
                  mb: 3,
                }}
              >
                <VerifiedIcon sx={{ color: colors.success, fontSize: 18 }} />
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: colors.success,
                  }}
                >
                  HIPAA Compliant Platform
                </Typography>
              </Box>

              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2.2rem", sm: "3rem", md: "3.5rem" },
                  fontWeight: 800,
                  lineHeight: 1.15,
                  mb: 3,
                  color: colors.text,
                  letterSpacing: "-0.02em",
                }}
              >
                Manage Medical{" "}
                <Box
                  component="span"
                  sx={{ color: colors.primary }}
                >
                  Field Operations
                </Box>{" "}
                from One Platform
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: "1.05rem", md: "1.2rem" },
                  color: colors.textSecondary,
                  lineHeight: 1.75,
                  maxWidth: 540,
                  mb: 4,
                }}
              >
                MedFlowHub helps home health agencies, mobile diagnostic providers,
                and medical service companies coordinate patient orders, optimize visit
                routes, and manage field staff — all while maintaining full HIPAA compliance.
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  mb: 5,
                }}
              >
                <Button
                  component={RouterLink}
                  to="/login"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    background: colors.primary,
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: "1rem",
                    px: 3.5,
                    py: 1.5,
                    borderRadius: "10px",
                    textTransform: "none",
                    "&:hover": {
                      background: colors.primaryDark,
                      boxShadow: "0 4px 16px rgba(3, 105, 161, 0.3)",
                    },
                  }}
                >
                  Get Started
                </Button>
                <Button
                  onClick={() =>
                    document
                      .getElementById("features")
                      .scrollIntoView({ behavior: "smooth" })
                  }
                  sx={{
                    background: colors.bgWhite,
                    border: `1px solid ${colors.border}`,
                    color: colors.text,
                    fontWeight: 600,
                    fontSize: "1rem",
                    px: 3.5,
                    py: 1.5,
                    borderRadius: "10px",
                    textTransform: "none",
                    "&:hover": {
                      background: colors.bgLight,
                      borderColor: colors.primary,
                      color: colors.primary,
                    },
                  }}
                >
                  Learn More
                </Button>
              </Box>

              {/* Trust indicators */}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                {[
                  "End-to-end encryption",
                  "Two-factor authentication",
                  "Role-based access control",
                ].map((item) => (
                  <Box
                    key={item}
                    sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                  >
                    <CheckIcon sx={{ color: colors.success, fontSize: 18 }} />
                    <Typography
                      sx={{ fontSize: 13, color: colors.textSecondary, fontWeight: 500 }}
                    >
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>

            {/* Hero Visual — Dashboard Preview */}
            <Grid  size={{ xs: 12, md: 5 }}>
              <Box
                sx={{
                  background: colors.bgWhite,
                  borderRadius: "16px",
                  border: `1px solid ${colors.border}`,
                  boxShadow: "0 8px 32px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)",
                  overflow: "hidden",
                }}
              >
                {/* Browser bar */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 2.5,
                    py: 1.5,
                    background: colors.bgLight,
                    borderBottom: `1px solid ${colors.border}`,
                  }}
                >
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", background: "#FF5F57" }} />
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", background: "#FEBC2E" }} />
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", background: "#28C840" }} />
                  <Box
                    sx={{
                      ml: 2,
                      flex: 1,
                      background: colors.bgWhite,
                      borderRadius: "6px",
                      px: 2,
                      py: 0.5,
                      border: `1px solid ${colors.border}`,
                    }}
                  >
                    <Typography sx={{ fontSize: 11, color: colors.textSecondary }}>
                      app.medflowhub.com/dashboard
                    </Typography>
                  </Box>
                </Box>

                {/* Dashboard content */}
                <Box sx={{ p: 3 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: colors.text, mb: 2 }}>
                    Today's Overview
                  </Typography>

                  <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
                    {[
                      { label: "Active Routes", value: "12", color: colors.primary },
                      { label: "Orders Today", value: "48", color: colors.accent },
                      { label: "Staff on Field", value: "18", color: colors.success },
                    ].map((stat, i) => (
                      <Grid key={i} size={{ xs: 4 }}>
                        <Box
                          sx={{
                            background: colors.bgLight,
                            borderRadius: "10px",
                            p: 1.5,
                            textAlign: "center",
                            border: `1px solid ${colors.border}`,
                          }}
                        >
                          <Typography
                            sx={{ fontSize: 22, fontWeight: 700, color: stat.color }}
                          >
                            {stat.value}
                          </Typography>
                          <Typography sx={{ fontSize: 10, color: colors.textSecondary, mt: 0.25 }}>
                            {stat.label}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Mini chart area */}
                  <Box
                    sx={{
                      height: 80,
                      background: colors.bgLight,
                      borderRadius: "10px",
                      border: `1px solid ${colors.border}`,
                      mb: 2,
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <svg width="100%" height="100%" viewBox="0 0 400 80" preserveAspectRatio="none">
                      <defs>
                        <linearGradient id="medChart" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor={colors.primary} stopOpacity="0.15" />
                          <stop offset="100%" stopColor={colors.primary} stopOpacity="0" />
                        </linearGradient>
                      </defs>
                      <path
                        d="M0,65 Q40,55 80,45 T160,35 T240,28 T320,22 T400,18 L400,80 L0,80 Z"
                        fill="url(#medChart)"
                      />
                      <path
                        d="M0,65 Q40,55 80,45 T160,35 T240,28 T320,22 T400,18"
                        fill="none"
                        stroke={colors.primary}
                        strokeWidth="2"
                      />
                    </svg>
                    <Typography
                      sx={{
                        position: "absolute",
                        top: 8,
                        left: 12,
                        fontSize: 10,
                        color: colors.textSecondary,
                        fontWeight: 600,
                      }}
                    >
                      Orders completed this week
                    </Typography>
                  </Box>

                  {/* Activity items */}
                  {[
                    { text: "Route #12 completed — 6 patients visited", status: "Completed", statusColor: colors.success },
                    { text: "New order received — Priority exam", status: "Pending", statusColor: "#D97706" },
                    { text: "Nurse Martinez checked in — Zone B", status: "Active", statusColor: colors.primary },
                  ].map((item, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        py: 1.25,
                        borderTop: i === 0 ? "none" : `1px solid ${colors.border}`,
                      }}
                    >
                      <Typography sx={{ fontSize: 12, color: colors.text }}>
                        {item.text}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 10,
                          fontWeight: 600,
                          color: item.statusColor,
                          background: `${item.statusColor}15`,
                          px: 1,
                          py: 0.25,
                          borderRadius: "4px",
                          whiteSpace: "nowrap",
                          ml: 1,
                        }}
                      >
                        {item.status}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box sx={{ py: 8, background: colors.bgWhite, borderTop: `1px solid ${colors.border}`, borderBottom: `1px solid ${colors.border}` }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {stats.map((stat, i) => (
              <Grid key={i} size={{ xs: 6, md: 3 }}>
                <Box
                  className={`med-animate stagger-${i + 1}`}
                  sx={{ textAlign: "center" }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: "2rem", md: "2.75rem" },
                      fontWeight: 800,
                      color: colors.primary,
                      letterSpacing: "-0.02em",
                    }}
                  >
                    <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                  </Typography>
                  <Typography
                    sx={{ color: colors.textSecondary, fontSize: 14, mt: 0.5, fontWeight: 500 }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* What Is MedFlowHub Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, background: colors.bgWhite }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid  size={{ xs: 12, md: 6 }}>
              <Box className="med-animate">
                <Typography
                  sx={{
                    color: colors.primary,
                    fontSize: 13,
                    fontWeight: 700,
                    letterSpacing: "1.5px",
                    textTransform: "uppercase",
                    mb: 1.5,
                  }}
                >
                  About the Platform
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: "1.75rem", md: "2.25rem" },
                    fontWeight: 700,
                    mb: 2.5,
                    letterSpacing: "-0.01em",
                    lineHeight: 1.25,
                  }}
                >
                  Built for Medical Service Companies That Operate in the Field
                </Typography>
                <Typography
                  sx={{
                    color: colors.textSecondary,
                    fontSize: "1.05rem",
                    lineHeight: 1.8,
                    mb: 3,
                  }}
                >
                  MedFlowHub is a CRM platform designed for organizations that send
                  medical professionals to patient locations — home health agencies,
                  mobile diagnostic companies, and traveling nurse services. It replaces
                  spreadsheets and disconnected tools with a single system for managing
                  the full lifecycle of a patient service order.
                </Typography>
                <Typography
                  sx={{
                    color: colors.textSecondary,
                    fontSize: "1.05rem",
                    lineHeight: 1.8,
                  }}
                >
                  From the moment a physician places an order to final report delivery,
                  MedFlowHub tracks every step: patient intake, staff assignment, route
                  planning, document collection, and compliance reporting.
                </Typography>
              </Box>
            </Grid>
            <Grid  size={{ xs: 12, md: 6 }}>
              <Box className="med-animate stagger-2">
                <Grid container spacing={2}>
                  {[
                    { icon: HospitalIcon, title: "Facility Management", desc: "Manage hospitals, clinics, and service locations with subscription tracking and contact details." },
                    { icon: AssignmentIcon, title: "Order Lifecycle", desc: "Track orders from creation through assignment, completion, and billing with full status history." },
                    { icon: RouteIcon, title: "Route Planning", desc: "Group patient visits by geography and assign optimized routes to reduce travel between stops." },
                    { icon: ShieldIcon, title: "Compliance Built In", desc: "Every action is logged. PHI is encrypted. Access is controlled by role. Audit-ready from day one." },
                  ].map((item, i) => (
                    <Grid key={i} size={{ xs: 12, sm: 6 }}>
                      <Box
                        sx={{
                          p: 2.5,
                          borderRadius: "12px",
                          border: `1px solid ${colors.border}`,
                          height: "100%",
                          transition: "border-color 0.2s, box-shadow 0.2s",
                          "&:hover": {
                            borderColor: colors.primary,
                            boxShadow: "0 2px 12px rgba(3,105,161,0.08)",
                          },
                        }}
                      >
                        <item.icon sx={{ color: colors.primary, fontSize: 28, mb: 1 }} />
                        <Typography sx={{ fontWeight: 600, fontSize: 15, mb: 0.5 }}>
                          {item.title}
                        </Typography>
                        <Typography sx={{ color: colors.textSecondary, fontSize: 13, lineHeight: 1.6 }}>
                          {item.desc}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box id="features" sx={{ py: { xs: 8, md: 12 }, background: colors.bgLight }}>
        <Container maxWidth="lg">
          <Box className="med-animate" sx={{ textAlign: "center", mb: 8 }}>
            <Typography
              sx={{
                color: colors.primary,
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: "1.5px",
                textTransform: "uppercase",
                mb: 1.5,
              }}
            >
              Platform Capabilities
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "1.75rem", md: "2.25rem" },
                fontWeight: 700,
                mb: 2,
                letterSpacing: "-0.01em",
              }}
            >
              Tools Designed for Healthcare Operations
            </Typography>
            <Typography
              sx={{
                color: colors.textSecondary,
                maxWidth: 600,
                mx: "auto",
                fontSize: "1.05rem",
                lineHeight: 1.7,
              }}
            >
              Every feature is built to address the specific challenges of coordinating
              medical services across multiple locations and providers.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {features.map((feature, i) => (
              <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
                <Box
                  className={`med-animate stagger-${i + 1}`}
                  sx={{
                    p: 3.5,
                    height: "100%",
                    background: colors.bgWhite,
                    borderRadius: "14px",
                    border: `1px solid ${colors.border}`,
                    transition: "border-color 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      borderColor: feature.color,
                      boxShadow: `0 4px 20px ${feature.color}15`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "10px",
                      background: feature.bg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2.5,
                    }}
                  >
                    <feature.icon sx={{ fontSize: 24, color: feature.color }} />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      mb: 1,
                      color: colors.text,
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: colors.textSecondary,
                      lineHeight: 1.7,
                      fontSize: 14,
                    }}
                  >
                    {feature.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Box id="how-it-works" sx={{ py: { xs: 8, md: 12 }, background: colors.bgWhite }}>
        <Container maxWidth="lg">
          <Box className="med-animate" sx={{ textAlign: "center", mb: 8 }}>
            <Typography
              sx={{
                color: colors.primary,
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: "1.5px",
                textTransform: "uppercase",
                mb: 1.5,
              }}
            >
              How It Works
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "1.75rem", md: "2.25rem" },
                fontWeight: 700,
                letterSpacing: "-0.01em",
              }}
            >
              From Order to Completion in Four Steps
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {steps.map((item, i) => (
              <Grid key={i} size={{ xs: 12, sm: 6, md: 3 }}>
                <Box className={`med-animate stagger-${i + 1}`}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      background: colors.primary,
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                      fontSize: "1.1rem",
                      mb: 2.5,
                    }}
                  >
                    {item.step}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      mb: 1,
                      color: colors.text,
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: colors.textSecondary,
                      lineHeight: 1.7,
                      fontSize: 14,
                    }}
                  >
                    {item.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Who It's For Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, background: colors.bgLight }}>
        <Container maxWidth="lg">
          <Box className="med-animate" sx={{ textAlign: "center", mb: 8 }}>
            <Typography
              sx={{
                color: colors.primary,
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: "1.5px",
                textTransform: "uppercase",
                mb: 1.5,
              }}
            >
              Who It's For
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "1.75rem", md: "2.25rem" },
                fontWeight: 700,
                letterSpacing: "-0.01em",
              }}
            >
              Built for Healthcare Organizations in the Field
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {[
              {
                title: "Home Health Agencies",
                desc: "Coordinate nursing visits, therapy sessions, and aide services across patient homes. Manage schedules, track visit completion, and ensure documentation compliance.",
                icon: HospitalIcon,
              },
              {
                title: "Mobile Diagnostic Providers",
                desc: "Route phlebotomists, EKG technicians, and imaging specialists to patient locations. Process physician orders and manage exam results securely.",
                icon: MedicalIcon,
              },
              {
                title: "Traveling Medical Services",
                desc: "Organize multi-stop routes for nurses and clinicians serving facilities. Track staff across territories and balance assignments by skill and availability.",
                icon: PeopleIcon,
              },
            ].map((item, i) => (
              <Grid key={i} size={{ xs: 12, md: 4 }}>
                <Box
                  className={`med-animate stagger-${i + 1}`}
                  sx={{
                    p: 4,
                    height: "100%",
                    background: colors.bgWhite,
                    borderRadius: "14px",
                    border: `1px solid ${colors.border}`,
                    borderTop: `3px solid ${colors.primary}`,
                  }}
                >
                  <item.icon sx={{ color: colors.primary, fontSize: 36, mb: 2 }} />
                  <Typography
                    sx={{
                      fontSize: "1.15rem",
                      fontWeight: 600,
                      mb: 1.5,
                      color: colors.text,
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: colors.textSecondary,
                      lineHeight: 1.75,
                      fontSize: 14,
                    }}
                  >
                    {item.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Contact Section */}
      <Box id="contact" sx={{ py: { xs: 8, md: 12 }, background: colors.bgWhite }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="flex-start">
            <Grid  size={{ xs: 12, md: 6 }}>
              <Box className="med-animate">
                <Typography
                  sx={{
                    color: colors.primary,
                    fontSize: 13,
                    fontWeight: 700,
                    letterSpacing: "1.5px",
                    textTransform: "uppercase",
                    mb: 1.5,
                  }}
                >
                  Get in Touch
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: "1.75rem", md: "2.25rem" },
                    fontWeight: 700,
                    mb: 2,
                    letterSpacing: "-0.01em",
                  }}
                >
                  Schedule a Demo or Ask a Question
                </Typography>
                <Typography
                  sx={{
                    color: colors.textSecondary,
                    fontSize: "1.05rem",
                    lineHeight: 1.8,
                    mb: 4,
                  }}
                >
                  Our team can walk you through the platform, help you evaluate
                  whether MedFlowHub fits your workflow, and get you set up.
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                  {[
                    { label: "support@medflowhub.com", Icon: EmailIcon },
                    { label: "(555) 123-4567", Icon: PhoneIcon },
                    { label: "Los Angeles, California", Icon: LocationIcon },
                  ].map((item, i) => (
                    <Box
                      key={i}
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "10px",
                          background: colors.primaryLight,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <item.Icon sx={{ color: colors.primary, fontSize: 20 }} />
                      </Box>
                      <Typography sx={{ color: colors.text, fontWeight: 500, fontSize: 15 }}>
                        {item.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>

            <Grid  size={{ xs: 12, md: 6 }}>
              <Box
                className="med-animate stagger-2"
                sx={{
                  p: 4,
                  background: colors.bgWhite,
                  borderRadius: "14px",
                  border: `1px solid ${colors.border}`,
                  boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                }}
              >
                {formSubmitted ? (
                  <Box sx={{ textAlign: "center", py: 6 }}>
                    <CheckIcon sx={{ fontSize: 52, color: colors.success, mb: 2 }} />
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 600, mb: 1 }}
                    >
                      Message Sent
                    </Typography>
                    <Typography sx={{ color: colors.textSecondary }}>
                      We'll get back to you within 24 hours.
                    </Typography>
                  </Box>
                ) : (
                  <Box component="form" onSubmit={handleContactSubmit}>
                    <Typography
                      sx={{
                        fontSize: "1.25rem",
                        fontWeight: 600,
                        mb: 3,
                        color: colors.text,
                      }}
                    >
                      Send Us a Message
                    </Typography>
                    {["name", "email", "message"].map((field) => (
                      <TextField
                        key={field}
                        required
                        fullWidth
                        label={
                          field === "name"
                            ? "Full Name"
                            : field === "email"
                            ? "Email Address"
                            : "Message"
                        }
                        name={field}
                        type={field === "email" ? "email" : "text"}
                        multiline={field === "message"}
                        rows={field === "message" ? 4 : 1}
                        value={contactForm[field]}
                        onChange={handleContactChange}
                        sx={{
                          mb: 2.5,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            "& fieldset": {
                              borderColor: colors.border,
                            },
                            "&:hover fieldset": {
                              borderColor: colors.primary,
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: colors.primary,
                            },
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: colors.primary,
                          },
                        }}
                      />
                    ))}
                    <Button
                      type="submit"
                      fullWidth
                      sx={{
                        background: colors.primary,
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: "1rem",
                        py: 1.5,
                        borderRadius: "10px",
                        textTransform: "none",
                        "&:hover": {
                          background: colors.primaryDark,
                          boxShadow: "0 4px 16px rgba(3,105,161,0.3)",
                        },
                      }}
                    >
                      Send Message
                    </Button>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: { xs: 8, md: 10 }, background: colors.primary }}>
        <Container maxWidth="md" sx={{ textAlign: "center" }}>
          <Box className="med-animate">
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "1.75rem", md: "2.25rem" },
                fontWeight: 700,
                mb: 2,
                color: "#fff",
                letterSpacing: "-0.01em",
              }}
            >
              Ready to Streamline Your Operations?
            </Typography>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.8)",
                fontSize: "1.1rem",
                mb: 4,
                maxWidth: 500,
                mx: "auto",
                lineHeight: 1.7,
              }}
            >
              See how MedFlowHub can help your team manage more patients
              with less administrative overhead.
            </Typography>
            <Button
              component={RouterLink}
              to="/login"
              endIcon={<ArrowForwardIcon />}
              sx={{
                background: "#fff",
                color: colors.primary,
                fontWeight: 600,
                fontSize: "1rem",
                px: 4,
                py: 1.5,
                borderRadius: "10px",
                textTransform: "none",
                "&:hover": {
                  background: "rgba(255,255,255,0.9)",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                },
              }}
            >
              Get Started
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ py: 5, borderTop: `1px solid ${colors.border}`, background: colors.bgLight }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid  size={{ xs: 12, md: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <MedicalIcon sx={{ color: colors.primary, fontSize: 24 }} />
                <Typography
                  sx={{
                    fontSize: "1.2rem",
                    fontWeight: 700,
                    color: colors.primary,
                  }}
                >
                  MedFlowHub
                </Typography>
              </Box>
              <Typography sx={{ color: colors.textSecondary, fontSize: 13 }}>
                &copy; {new Date().getFullYear()} MedFlowHub. All rights reserved.
              </Typography>
            </Grid>
            <Grid  size={{ xs: 12, md: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: { xs: "flex-start", md: "center" },
                  gap: 3,
                }}
              >
                {["Privacy Policy", "Terms of Service", "HIPAA Compliance", "Security"].map((item) => (
                  <Typography
                    key={item}
                    component="a"
                    href="#"
                    sx={{
                      color: colors.textSecondary,
                      fontSize: 13,
                      fontWeight: 500,
                      textDecoration: "none",
                      "&:hover": { color: colors.primary },
                    }}
                  >
                    {item}
                  </Typography>
                ))}
              </Box>
            </Grid>
            <Grid  size={{ xs: 12, md: 4 }}>
              <Typography
                sx={{
                  color: colors.textSecondary,
                  fontSize: 13,
                  textAlign: { xs: "left", md: "right" },
                }}
              >
                HIPAA Compliant &bull; SOC 2 Ready &bull; 256-bit Encryption
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
